import { useCallback, useEffect, useRef, useState } from 'react';
import { ImpulseSpinner } from 'react-spinners-kit';
import PropTypes from 'prop-types';
import cn from 'classnames';

import askAnythingChatIcon from 'assets/ask-anything-chat.svg';
import retryIcon from 'assets/rotate_right_arrow.svg';
import thinkingSpinner from 'assets/thinking-spinner.svg';

import { ASK_ANYTHING_PROMPTS } from 'Pages/TeamDashboard/CreateReview/components/constants';
import OverviewReport from 'Pages/TeamDashboard/CreateReview/components/OverviewReport';

import {
  getConversationMessages,
  sendChatMessage,
} from 'services/conversationService';

import { PROTOTYPE_OPERATIONS } from 'utlis/chatPrototype';
import { mapChatMessage, parseOverviewData } from 'utlis/parseChatOverview';

import ChatMessageContent from './ChatMessageContent';
import CopyButton from './CopyButton';
import { UserMessageContent } from './UserMessageContent';

import styles from './styles.module.scss';

const getDateLabel = dateStr => {
  if (!dateStr) {
    return null;
  }

  const date = new Date(dateStr);
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const yesterday = new Date(today);

  yesterday.setDate(yesterday.getDate() - 1);

  const messageDate = new Date(
    date.getFullYear(),
    date.getMonth(),
    date.getDate()
  );

  if (messageDate.getTime() === today.getTime()) {
    return 'Today';
  }

  if (messageDate.getTime() === yesterday.getTime()) {
    return 'Yesterday';
  }

  const day = date.getDate();
  const months = [
    'Jan',
    'Feb',
    'Mar',
    'Apr',
    'May',
    'Jun',
    'Jul',
    'Aug',
    'Sep',
    'Oct',
    'Nov',
    'Dec',
  ];

  return `${day} ${months[date.getMonth()]}`;
};

export const AskAnything = ({
  onSendMessage,
  inputValue,
  setInputValue,
  isHovering,
  setIsHovering,
  onThinkingChange,
  designReviewUuid,
  workspaceRole,
  conversationId,
  setConversationId,
  chatMessages,
  setChatMessages,
  chatHasMore,
  setChatHasMore,
  chatNextBeforeSeq,
  setChatNextBeforeSeq,
  streamingContent,
  setStreamingContent,
  isChatThinking,
  setIsChatThinking,
  isConversationLoaded = true,
  onNavigateToFeedback,
  newIdeaOptions = [],
  prototypeJobs = [],
  prototypeNotice = '',
  onPrototypeEvent,
  onStreamInterrupted,
}) => {
  const [isLoadingOlder, setIsLoadingOlder] = useState(false);

  const isThinking = isChatThinking;
  const setIsThinking = setIsChatThinking;

  const conversationEndRef = useRef(null);
  const containerRef = useRef(null);
  const committedValueRef = useRef('');
  const abortControllerRef = useRef(null);
  const shouldScrollRef = useRef(false);
  const isInitialRenderRef = useRef(true);
  const isLoadingOlderRef = useRef(false);
  const prevScrollHeightRef = useRef(0);
  const userScrolledUpRef = useRef(false);
  const isRefreshingRef = useRef(false);
  const turnIdRef = useRef(0);
  const refreshTimeoutRef = useRef(null);

  // Typewriter effect: buffer holds words not yet displayed, RAF drains them
  const wordBufferRef = useRef([]);
  const displayedTextRef = useRef('');
  const rafIdRef = useRef(null);
  const lastWordTimeRef = useRef(0);
  const streamDoneRef = useRef(false);
  const lastSentMessageRef = useRef('');

  const WORD_INTERVAL_MS = 30;

  const startTypewriter = useCallback(() => {
    if (rafIdRef.current) {
      return;
    }

    const tick = timestamp => {
      if (wordBufferRef.current.length > 0) {
        const elapsed = timestamp - lastWordTimeRef.current;

        if (elapsed >= WORD_INTERVAL_MS) {
          const wordsToShow = Math.min(
            Math.max(1, Math.floor(elapsed / WORD_INTERVAL_MS)),
            wordBufferRef.current.length
          );
          const nextWords = wordBufferRef.current.splice(0, wordsToShow);

          displayedTextRef.current += nextWords.join('');
          setStreamingContent(displayedTextRef.current);
          lastWordTimeRef.current = timestamp;
        }
      }

      if (wordBufferRef.current.length > 0 || !streamDoneRef.current) {
        rafIdRef.current = requestAnimationFrame(tick);
      }
      else {
        rafIdRef.current = null;
      }
    };

    rafIdRef.current = requestAnimationFrame(tick);
  }, [setStreamingContent]);

  const stopTypewriter = useCallback(() => {
    if (rafIdRef.current) {
      cancelAnimationFrame(rafIdRef.current);
      rafIdRef.current = null;
    }
  }, []);

  const flushTypewriter = useCallback(() => {
    stopTypewriter();

    if (wordBufferRef.current.length > 0) {
      displayedTextRef.current += wordBufferRef.current.join('');
      wordBufferRef.current = [];
      setStreamingContent(displayedTextRef.current);
    }
  }, [stopTypewriter, setStreamingContent]);

  const resetTypewriter = useCallback(() => {
    stopTypewriter();
    wordBufferRef.current = [];
    displayedTextRef.current = '';
    streamDoneRef.current = false;
    lastWordTimeRef.current = 0;
  }, [stopTypewriter]);

  useEffect(() => {
    if (onThinkingChange) {
      onThinkingChange(isThinking);
    }
  }, [isThinking, onThinkingChange]);

  const scrollToBottom = useCallback(() => {
    const container = containerRef.current;

    if (container) {
      container.scrollTop = container.scrollHeight;
    }
  }, []);

  // Initial render: scroll to bottom instantly without visible animation
  useEffect(() => {
    const canScroll
      = chatMessages.length > 0
      && isInitialRenderRef.current
      && containerRef.current
      && !isLoadingOlderRef.current
      && !isRefreshingRef.current;

    if (canScroll) {
      isInitialRenderRef.current = false;
      scrollToBottom();
    }
  }, [chatMessages, scrollToBottom]);

  // During streaming: keep scroll at bottom ONLY if user hasn't scrolled up
  useEffect(() => {
    if (
      streamingContent
      && containerRef.current
      && !userScrolledUpRef.current
    ) {
      containerRef.current.scrollTop = containerRef.current.scrollHeight;
    }
  }, [streamingContent]);

  useEffect(() => {
    if ((prototypeJobs.length > 0 || prototypeNotice) && containerRef.current && !userScrolledUpRef.current) {
      scrollToBottom();
    }
  }, [prototypeJobs.length, prototypeNotice, scrollToBottom]);

  // When user sends a message, scroll to bottom and reset scroll-up flag
  useEffect(() => {
    if (shouldScrollRef.current && containerRef.current) {
      shouldScrollRef.current = false;
      userScrolledUpRef.current = false;
      containerRef.current.scrollTop = containerRef.current.scrollHeight;
    }
  }, [chatMessages]);

  // Detect user scrolling up during streaming to stop auto-scroll
  useEffect(() => {
    const container = containerRef.current;

    if (!container) {
      return;
    }

    const handleUserScroll = () => {
      if (isLoadingOlderRef.current) {
        return;
      }

      const distanceFromBottom
        = container.scrollHeight - container.scrollTop - container.clientHeight;

      if (distanceFromBottom > 150) {
        userScrolledUpRef.current = true;
      }
      else {
        userScrolledUpRef.current = false;
      }
    };

    container.addEventListener('scroll', handleUserScroll, { passive: true });

    return () => container.removeEventListener('scroll', handleUserScroll);
  }, []);

  // Load older messages when user scrolls to top
  const loadOlderMessages = useCallback(async () => {
    if (
      !chatHasMore
      || isLoadingOlder
      || !conversationId
      || !chatNextBeforeSeq
    ) {
      return;
    }

    isLoadingOlderRef.current = true;
    setIsLoadingOlder(true);

    const container = containerRef.current;

    prevScrollHeightRef.current = container ? container.scrollHeight : 0;

    try {
      const response = await getConversationMessages(
        conversationId,
        20,
        chatNextBeforeSeq
      );

      const olderMessages = response.messages.map(mapChatMessage);

      setChatMessages(prev => [...olderMessages, ...prev]);
      setChatHasMore(response.has_more);
      setChatNextBeforeSeq(response.next_before_sequence);

      // Restore scroll position after prepend
      requestAnimationFrame(() => {
        if (container) {
          const newScrollHeight = container.scrollHeight;
          const addedHeight = newScrollHeight - prevScrollHeightRef.current;

          container.scrollTop = addedHeight;
        }

        isLoadingOlderRef.current = false;
      });
    }
    catch {
      isLoadingOlderRef.current = false;
    }
    finally {
      setIsLoadingOlder(false);
    }
  }, [
    chatHasMore,
    isLoadingOlder,
    conversationId,
    chatNextBeforeSeq,
    setChatMessages,
    setChatHasMore,
    setChatNextBeforeSeq,
  ]);

  // Detect scroll to top
  useEffect(() => {
    const container = containerRef.current;

    if (!container) {
      return;
    }

    const handleScroll = () => {
      if (container.scrollTop === 0 && chatHasMore && !isLoadingOlder) {
        loadOlderMessages();
      }
    };

    container.addEventListener('scroll', handleScroll, { passive: true });

    return () => container.removeEventListener('scroll', handleScroll);
  }, [chatHasMore, isLoadingOlder, loadOlderMessages]);

  const handlePromptClick = useCallback(
    item => {
      committedValueRef.current = item.prompt;
      setIsHovering(false);
      setInputValue(item.prompt);
    },
    [setInputValue, setIsHovering]
  );

  const handlePromptHover = useCallback(
    item => {
      setIsHovering(true);
      setInputValue(item.prompt);
    },
    [setInputValue, setIsHovering]
  );

  const handlePromptLeave = useCallback(() => {
    setIsHovering(false);
    setInputValue(committedValueRef.current);
  }, [setInputValue, setIsHovering]);

  const refreshMessages = useCallback(
    async convId => {
      if (!convId) {
        return;
      }

      isRefreshingRef.current = true;
      const turnId = turnIdRef.current;
      const container = containerRef.current;
      const wasAtBottom = container
        ? container.scrollHeight
            - container.scrollTop
            - container.clientHeight
          < 50
        : true;
      const scrollPos = container ? container.scrollTop : 0;

      try {
        const response = await getConversationMessages(convId);

        // A newer message was sent meanwhile: the saved messages do not have it yet, so keep what is on screen
        if (turnId !== turnIdRef.current) {
          isRefreshingRef.current = false;

          return;
        }

        setChatMessages(response.messages.map(mapChatMessage));
        setChatHasMore(response.has_more);
        setChatNextBeforeSeq(response.next_before_sequence);

        requestAnimationFrame(() => {
          if (container) {
            if (wasAtBottom) {
              container.scrollTop = container.scrollHeight;
            }
            else {
              container.scrollTop = scrollPos;
            }
          }

          isRefreshingRef.current = false;
        });
      }
      catch {
        isRefreshingRef.current = false;
      }
    },
    [setChatMessages, setChatHasMore, setChatNextBeforeSeq]
  );

  // Loads the saved messages shortly after a reply is shown (a new message sent before that cancels it)
  const scheduleRefresh = useCallback(convId => {
    clearTimeout(refreshTimeoutRef.current);
    refreshTimeoutRef.current = setTimeout(() => refreshMessages(convId), 500);
  }, [refreshMessages]);

  const streamResponse = useCallback(
    async messageText => {
      turnIdRef.current += 1;
      clearTimeout(refreshTimeoutRef.current);
      setIsThinking(true);
      setStreamingContent('');
      resetTypewriter();

      // eslint-disable-next-line compat/compat
      const controller = new AbortController();

      abortControllerRef.current = controller;

      try {
        await sendChatMessage({
          designReviewUuid,
          message: messageText,
          conversationId,
          role: workspaceRole,
          onDelta: delta => {
            const words = delta.split(/(\s+)/);

            wordBufferRef.current.push(...words);
            startTypewriter();
          },
          onError: errorMsg => {
            flushTypewriter();
            setChatMessages(prev => [
              ...prev,
              {
                id: `error-${Date.now()}`,
                role: 'assistant',
                content: errorMsg,
                created_at: new Date().toISOString(),
                isError: true,
              },
            ]);
          },
          onPrototypeEvent,
          onDone: (fullText, newConversationId, extra = {}) => {
            streamDoneRef.current = true;

            if (extra.interrupted && onStreamInterrupted) {
              onStreamInterrupted();
            }

            const overview = extra.overview || parseOverviewData(fullText);

            const finishOverviewResponse = () => {
              if (newConversationId) {
                setConversationId(newConversationId);
              }
              setStreamingContent('');
              resetTypewriter();

              if (overview) {
                setChatMessages(prev => [
                  ...prev,
                  {
                    id: `assistant-${Date.now()}`,
                    role: 'assistant',
                    type: 'overview_report',
                    overview,
                    content: fullText || '',
                    created_at: new Date().toISOString(),
                  },
                ]);
                setIsThinking(false);
                scheduleRefresh(newConversationId);

                return;
              }

              setTimeout(() => {
                refreshMessages(newConversationId).finally(() => {
                  setIsThinking(false);
                });
              }, 500);
            };

            if (overview || extra.source === 'overview' || !fullText) {
              finishOverviewResponse();

              return;
            }

            const waitForFlush = () => {
              if (wordBufferRef.current.length === 0) {
                const finalText = displayedTextRef.current || fullText;

                if (newConversationId) {
                  setConversationId(newConversationId);
                }

                setChatMessages(prev => [
                  ...prev,
                  {
                    id: `assistant-${Date.now()}`,
                    role: 'assistant',
                    content: finalText,
                    created_at: new Date().toISOString(),
                  },
                ]);

                setStreamingContent('');
                setIsThinking(false);
                resetTypewriter();

                scheduleRefresh(newConversationId);
              }
              else {
                requestAnimationFrame(waitForFlush);
              }
            };

            requestAnimationFrame(waitForFlush);
          },
          signal: controller.signal,
        });
      }
      catch (err) {
        flushTypewriter();

        if (err.name !== 'AbortError') {
          setChatMessages(prev => [
            ...prev,
            {
              id: `error-${Date.now()}`,
              role: 'assistant',
              content: 'Something went wrong. Please try again.',
              created_at: new Date().toISOString(),
              isError: true,
            },
          ]);
        }

        setStreamingContent('');
        setIsThinking(false);
        resetTypewriter();
      }
      finally {
        abortControllerRef.current = null;
      }
    },
    [
      designReviewUuid,
      workspaceRole,
      conversationId,
      setChatMessages,
      setConversationId,
      refreshMessages,
      scheduleRefresh,
      onPrototypeEvent,
      onStreamInterrupted,
      startTypewriter,
      flushTypewriter,
      resetTypewriter,
      setStreamingContent,
      setIsThinking,
    ]
  );

  // Sends the typed message, or the given one (the message built from the options picked for a variant)
  const handleSend = useCallback(async givenMessage => {
    const isGivenMessage = typeof givenMessage === 'string';
    const messageText = (isGivenMessage ? givenMessage : inputValue).trim();

    if (!isConversationLoaded || !messageText || isHovering || isThinking) {
      return;
    }

    lastSentMessageRef.current = messageText;

    // A given message leaves whatever is typed in the input alone
    if (!isGivenMessage) {
      committedValueRef.current = '';
      setInputValue('');
    }

    const userMessage = {
      id: `user-${Date.now()}`,
      role: 'user',
      content: messageText,
      created_at: new Date().toISOString(),
    };

    shouldScrollRef.current = true;
    setChatMessages(prev => [...prev, userMessage]);

    await streamResponse(messageText);
  }, [
    isConversationLoaded,
    inputValue,
    setInputValue,
    isHovering,
    isThinking,
    setChatMessages,
    streamResponse,
  ]);

  const handleRetry = useCallback(async () => {
    const messageToRetry = lastSentMessageRef.current;

    if (!messageToRetry || isThinking) {
      return;
    }

    setChatMessages(prev => prev.filter(msg => !msg.isError));

    await streamResponse(messageToRetry);
  }, [isThinking, setChatMessages, streamResponse]);

  useEffect(() => {
    if (onSendMessage) {
      onSendMessage.current = handleSend;
    }
  }, [handleSend, onSendMessage]);

  // Keep committedValueRef in sync when user types directly
  useEffect(() => {
    if (!isHovering) {
      committedValueRef.current = inputValue;
    }
  }, [inputValue, isHovering]);

  // Reset initial render flag when component remounts with existing messages
  useEffect(() => {
    if (chatMessages.length > 0) {
      isInitialRenderRef.current = true;
    }
  }, []);

  const hasConversation = chatMessages.length > 0;
  const lastUserMessageIndex = chatMessages.map(message => message.role).lastIndexOf('user');

  if (!isConversationLoaded) {
    return (
      <div className={styles['chat-loading']}>
        <ImpulseSpinner frontColor="#28BDAE" />
      </div>
    );
  }

  if (!hasConversation) {
    return (
      <div className={styles['ask-anything-container']}>
        <div className={styles['ask-anything-header']}>
          <img
            src={askAnythingChatIcon}
            alt="Ask anything"
            className={styles['chat-icon']}
          />
          <h2 className={styles['ask-anything-title']}>
            {'Ask anything'}
          </h2>
          <p className={styles['ask-anything-subtitle']}>
            Solve any question or pick a trade-off
            <br />
            to make your next product decision
          </p>
        </div>
        <div className={styles['prompts-list']}>
          {ASK_ANYTHING_PROMPTS.map(item => (
            <button
              key={item.id}
              type="button"
              className={styles['prompt-button']}
              onClick={() => handlePromptClick(item)}
              onMouseEnter={() => handlePromptHover(item)}
              onMouseLeave={handlePromptLeave}
            >
              <img
                src={item.icon}
                alt={item.label}
                className={styles['prompt-icon']}
              />
              <span className={styles['prompt-label']}>
                {item.label}
              </span>
            </button>
          ))}
        </div>
      </div>
    );
  }

  // "Generating prototype..." while the prototype started in the last turn is being generated
  const renderPrototypeRows = () => prototypeJobs.map(job => (
    <div key={job.processUuid} className={styles['thinking-indicator']}>
      <img
        src={thinkingSpinner}
        alt="Generating"
        className={styles['thinking-spinner']}
      />
      <span className={cn(styles['thinking-text'], styles['prototype-progress-text'])}>
        {job.operation === PROTOTYPE_OPERATIONS.REFINE ? 'Generating variant...' : 'Generating prototype...'}
      </span>
    </div>
  ));

  // Group messages by date for date separators
  const renderMessages = () => {
    let lastDateLabel = null;
    const elements = [];

    chatMessages.forEach((message, messageIndex) => {
      const dateLabel = getDateLabel(message.created_at);

      if (dateLabel && dateLabel !== lastDateLabel) {
        lastDateLabel = dateLabel;
        elements.push(
          <div key={`date-${message.id}`} className={styles['date-separator']}>
            <div className={styles['date-separator-line']} />
            <span className={styles['date-separator-text']}>
              {dateLabel}
            </span>
            <div className={styles['date-separator-line']} />
          </div>
        );
      }

      const isOverviewReport = message.type === 'overview_report' && message.overview;

      elements.push(
        <div
          key={message.id}
          className={cn(
            styles.message,
            message.role === 'user'
              ? styles['message--user']
              : styles['message--assistant'],
            isOverviewReport && styles['message--overview']
          )}
        >
          {message.role === 'user'
            ? (
              <div className={styles['message-bubble--user']}>
                <UserMessageContent content={message.content} mentionOptions={newIdeaOptions} />
              </div>
            )
            : isOverviewReport
              ? (
                <OverviewReport overview={message.overview} />
              )
              : (
                <>
                  <div className={styles['message-bubble--assistant']}>
                    <ChatMessageContent
                      content={message.content}
                      onNavigateToFeedback={onNavigateToFeedback}
                    />
                  </div>
                  {message.isError
                    ? (
                      <button
                        type="button"
                        className={styles['retry-button']}
                        onClick={handleRetry}
                      >
                        <span className={styles['retry-text']}>
                          Retry
                        </span>
                        <img
                          src={retryIcon}
                          alt="Retry"
                          className={styles['retry-icon']}
                        />
                      </button>
                    )
                    : (
                      <CopyButton content={message.content} />
                    )}
                </>
              )}
        </div>
      );

      if (messageIndex === lastUserMessageIndex) {
        elements.push(...renderPrototypeRows());
      }
    });

    if (lastUserMessageIndex === -1) {
      elements.push(...renderPrototypeRows());
    }

    return elements;
  };

  return (
    <div ref={containerRef} className={styles['conversation-container']}>
      {isLoadingOlder && (
        <div className={styles['thinking-indicator']}>
          <img
            src={thinkingSpinner}
            alt="Loading"
            className={styles['thinking-spinner']}
          />
          <span className={styles['thinking-text']}>
            {'Loading...'}
          </span>
        </div>
      )}
      {renderMessages()}
      {isThinking && streamingContent && (
        <div className={cn(styles.message, styles['message--assistant'])}>
          <div className={styles['message-bubble--assistant']}>
            <ChatMessageContent
              content={streamingContent}
              isStreaming
              onNavigateToFeedback={onNavigateToFeedback}
            />
          </div>
        </div>
      )}
      {isThinking && !streamingContent && prototypeJobs.length === 0 && (
        <div className={styles['thinking-indicator']}>
          <img
            src={thinkingSpinner}
            alt="Thinking"
            className={styles['thinking-spinner']}
          />
          <span className={styles['thinking-text']}>
            {'Thinking...'}
          </span>
        </div>
      )}
      {prototypeNotice && (
        <div className={cn(styles.message, styles['message--assistant'])}>
          <p className={styles['prototype-notice']}>
            {prototypeNotice}
          </p>
        </div>
      )}
      <div ref={conversationEndRef} />
    </div>
  );
};

AskAnything.propTypes = {
  onSendMessage: PropTypes.shape({ current: PropTypes.func }),
  inputValue: PropTypes.string.isRequired,
  setInputValue: PropTypes.func.isRequired,
  isHovering: PropTypes.bool.isRequired,
  setIsHovering: PropTypes.func.isRequired,
  onThinkingChange: PropTypes.func,
  designReviewUuid: PropTypes.string,
  workspaceRole: PropTypes.string,
  conversationId: PropTypes.string,
  setConversationId: PropTypes.func.isRequired,
  chatMessages: PropTypes.array.isRequired,
  setChatMessages: PropTypes.func.isRequired,
  chatHasMore: PropTypes.bool.isRequired,
  setChatHasMore: PropTypes.func.isRequired,
  chatNextBeforeSeq: PropTypes.number,
  setChatNextBeforeSeq: PropTypes.func.isRequired,
  streamingContent: PropTypes.string.isRequired,
  setStreamingContent: PropTypes.func.isRequired,
  isChatThinking: PropTypes.bool.isRequired,
  setIsChatThinking: PropTypes.func.isRequired,
  isConversationLoaded: PropTypes.bool,
  onNavigateToFeedback: PropTypes.func,
  newIdeaOptions: PropTypes.array,
  prototypeJobs: PropTypes.arrayOf(PropTypes.shape({
    processUuid: PropTypes.string.isRequired,
    operation: PropTypes.string,
  })),
  prototypeNotice: PropTypes.string,
  onPrototypeEvent: PropTypes.func,
  onStreamInterrupted: PropTypes.func,
};

export default AskAnything;
